/**
 * Tiny cookie-consent global API.
 *
 * Future scripts (Google Analytics, Hotjar, Meta Pixel, etc.) MUST check
 * `window.cookieConsent.has('analytics')` before loading. Until consent is
 * granted, no tracker may set cookies or send any network request.
 *
 * Storage shape:
 * {
 *   essential: true,           // always true (strictly necessary)
 *   functional: bool,          // preferences, region filter, layout
 *   analytics: bool,           // GA, Matomo, etc.
 *   marketing: bool,           // Hotjar, Meta pixel, ads
 *   policy_version: number,
 *   client_id: uuid,           // anon identifier (for audit log)
 *   updated_at: iso8601,
 *   action: 'accept_all' | 'reject_all' | 'save_custom'
 * }
 */

const STORAGE_KEY = 'terroir_cookie_consent_v2';
const LEGACY_KEY = 'terroir_cookie_consent';  // v1 banner (accepted/declined)

const DEFAULT_STATE = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  policy_version: null,
  client_id: null,
  updated_at: null,
  action: null,
};

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_STATE, ...parsed, essential: true };
    }
    // Migrate v1 → v2 (best effort: 'accepted' means all opt-in, 'declined' means essentials only)
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === 'accepted') {
      return { ...DEFAULT_STATE, functional: true, analytics: true, marketing: true, action: 'accept_all' };
    }
    if (legacy === 'declined') {
      return { ...DEFAULT_STATE, action: 'reject_all' };
    }
  } catch {
    /* ignore */
  }
  return null;  // no consent stored yet
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota exceeded or private mode */ }
}

const listeners = new Set();

function notify() {
  listeners.forEach(fn => { try { fn(currentState()); } catch { /* ignore */ } });
  window.dispatchEvent(new CustomEvent('cookie-consent-changed', { detail: currentState() }));
}

let _cache = null;  // memoized state, null until first load

function currentState() {
  if (_cache === null) _cache = loadFromStorage();
  return _cache || { ...DEFAULT_STATE };
}

function has(category) {
  if (category === 'essential') return true;
  const s = currentState();
  return !!s[category];
}

function hasMadeChoice() {
  const s = loadFromStorage();
  return !!(s && s.action);
}

function setAll({ functional, analytics, marketing, action, policy_version, client_id }) {
  _cache = {
    essential: true,
    functional: !!functional,
    analytics: !!analytics,
    marketing: !!marketing,
    policy_version: policy_version ?? currentState().policy_version,
    client_id: client_id ?? currentState().client_id,
    updated_at: new Date().toISOString(),
    action: action || 'save_custom',
  };
  saveToStorage(_cache);
  notify();
  return _cache;
}

function reset() {
  _cache = null;
  try { localStorage.removeItem(STORAGE_KEY); localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
  notify();
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const cookieConsent = {
  has,
  hasMadeChoice,
  get: () => ({ ...currentState() }),
  setAll,
  reset,
  subscribe,
};

// Expose globally so future GA/Hotjar scripts can call window.cookieConsent.has('analytics')
if (typeof window !== 'undefined') {
  window.cookieConsent = cookieConsent;
}

export default cookieConsent;
